import { AnyEventObject } from 'xstate';
import { sendDeleteConfirmation } from '~/features/utils';
import { JobListing } from '~/types/hlx/jobListing';
import {
  Context,
  OnAddListingsEvent,
  OnDeleteEvent,
  OnEditEvent,
  OnGetDoneEvent,
  OnSendDeleteConfirmationEvent,
  OnUpdateFacilitiesEvent,
  OnUpdateFacilityStepEvent,
  OnUpdateListingsEvent,
  Scope,
} from './types';

export const openModal = (): Partial<Context> => ({
  modal: {
    edit: false,
    create: true,
  },
});

export const closeModal = (): Partial<Context> => ({
  modal: {
    edit: false,
    create: false,
  },
  currentStep: 0,
  listing: undefined,
});

export const setListings = ({ event }: Scope): Partial<Context> => ({
  listings: (event as OnGetDoneEvent).output,
});

export const addListing = (scope: Scope): Partial<Context> => {
  const event = scope.event as unknown as OnAddListingsEvent;
  return {
    listings: [...scope.context.listings, event.payload.output],
    listing: event.payload.output,
  };
};

// [MCV-1523] `listing` must be refreshed alongside `listings`. Every wizard step
// hydrates its form from `context.listing`, so updating only the collection left
// the singular record frozen at whatever it was when the listing was created —
// stepping forward then back re-mounted the form against stale data and fields
// like Provider Specialty came back empty. Re-fetching the listing (via "edit
// listing") showed the value correctly, which is what made this look like a
// save bug rather than a state-sync one.
export const updateListing = (scope: Scope): Partial<Context> => {
  const event = scope.event as unknown as OnUpdateListingsEvent;
  return {
    listings: scope.context.listings.map((listing) => {
      if (listing.id === event.payload.output.id) {
        return event.payload.output;
      }
      return listing;
    }),
    listing: event.payload.output,
  };
};

export const setListingToEdit = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnEditEvent;
  const listing = scope.context.listings.find((listing) => listing.id === event.payload.id);
  return {
    listing: listing,
    modal: {
      edit: true,
      create: false,
    },
  };
};

export const setListingToDelete = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnDeleteEvent;
  return {
    idToDelete: event.payload.id,
  };
};

const syncCurrentListing = (context: Context, listing: JobListing): Partial<Context> => ({
  listing,
  listings: context.listings.map((item) => (item.id === listing.id ? listing : item)),
});

export const setListingFacilities = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnUpdateFacilitiesEvent;
  const listing = {
    ...(scope.context.listing as JobListing),
    facilities: event.payload.values,
  };
  return syncCurrentListing(scope.context, listing);
};

export const setListingStates = (scope: Scope): Partial<Context> => {
  const event = scope.event as { type: string; payload: { values: string[] } };
  const listing = {
    ...(scope.context.listing as JobListing),
    stateCodes: event.payload.values,
  };
  return syncCurrentListing(scope.context, listing);
};

export const setListingFacilityStep = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnUpdateFacilityStepEvent;
  const currentListing = scope.context.listing as JobListing;
  const listing = {
    ...currentListing,
    facilities: event.payload.facilities,
    stateCodes: event.payload.stateCodes,
    // [MCV-1523] The step PATCHes primaryEmployerFacilityId to the API, but
    // the value never made it back into context — so FacilityForm, which
    // hydrates Primary Facility from exactly this field, found nothing on
    // back-navigation and rendered the control empty.
    //
    // The fallback only applies while facilities remain. An empty list cannot
    // have a primary: JobListingFacilitiesFormSchema deliberately allows the
    // primary to be absent once facilities are cleared (a states-only
    // listing), so absence there is a decision, not a value the step forgot
    // to send. Preserving the old id stranded the listing on a facility it no
    // longer had.
    primaryEmployerFacilityId: event.payload.facilities?.length
      ? (event.payload.primaryFacility ?? currentListing?.primaryEmployerFacilityId)
      : undefined,
  };
  return syncCurrentListing(scope.context, listing);
};

export const addDetail = (scope: Scope): Partial<Context> => {
  const event = scope.event as AnyEventObject;
  const listing = {
    ...(scope.context.listing as JobListing),
    detail: event.payload.output,
  };
  return syncCurrentListing(scope.context, listing);
};

// PATCH response -> merge into context.listing.detail -> Back remount -> form defaults.
// Present response fields overwrite prior context; omitted fields retain prior values.
export const updateDetail = (scope: Scope): Partial<Context> => {
  const event = scope.event as AnyEventObject;
  const listing = {
    ...(scope.context.listing as JobListing),
    detail: { ...scope.context.listing?.detail, ...event.payload.output },
  };
  return syncCurrentListing(scope.context, listing);
};

export const deleteListing = ({ context }: Scope): Partial<Context> => ({
  listings: context.listings.filter((listing) => listing.id !== context.idToDelete),
});

export const setLoading = (): Partial<Context> => ({
  loading: true,
});

export const resetLoading = (): Partial<Context> => ({
  loading: false,
});

export const nextStep = ({ context }: { context: Context }): Partial<Context> => ({
  currentStep: context.currentStep + 1,
});

export const prevStep = ({ context }: { context: Context }): Partial<Context> => ({
  currentStep: context.currentStep - 1,
});

export const resetStep = (): Partial<Context> => ({
  currentStep: 0,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openDeleteConfirmation = (args: any) => {
  const { event, system, self } = args;
  const deleteEvent = event as OnSendDeleteConfirmationEvent;
  const onConfirm = (id: number) => {
    self.send({ type: 'ON_DELETE', payload: { id } });
  };

  const userActor = system.get('userState');
  if (userActor) {
    const confirmationEvent = sendDeleteConfirmation(() => onConfirm(deleteEvent.payload.id));
    userActor.send(confirmationEvent);
  } else {
    console.error('[jobListing] Unable to send confirmation - userState not found in system', {
      availableActors: Object.keys(system),
      systemType: typeof system,
    });
  }
};
